import { useState, useEffect, useCallback, useRef } from 'react';
import {
  Modal,
  Form,
  Input,
  InputNumber,
  Select,
  Radio,
  TreeSelect,
  Button,
  Collapse,
  message,
  Segmented,
  Tooltip,
} from 'antd';
import {
  getSceneQcTaskTypeId,
  getSceneQcDefaultPackageUrl,
  getSceneTree,
  getSceneInspectionList,
  resolveSceneIdsByNames as resolveSceneIdsByNamesApi,
  getJobBazelPresets,
  createJob,
  createBazelJob,
  BazelPresets,
  SceneTreeNode,
  BuildType,
  BuildSpec,
  CreateJobParams,
  CreateBazelJobParams,
  JobItem,
} from '@apis/simulation';
import { detectAlgoVersionPreset } from '@/utils/versionTag';

/** 本 Modal 固定任务类型 = SCENE_QC（场景质检子链路），前端不可切换。 */
const SCENE_QC_TASK_TYPE_NAME = 'SCENE_QC';
const SCENE_QC_TASK_TYPE_LABEL = '场景质检 (SCENE_QC)';

// 前端 UI 类型 3 项：precompiled（合并原「导包」+「版本号」）+ 后端 package_bazel + compile_platform。
// precompiled 提交时映射回后端 package_neodata + algorithmVersion；
// 内部再用「版本号 / S3 地址」子选项卡区分（S3 地址走 bos:// 完整路径，
// PackageUrlResolver 会透传不套模板，与短版本号复用同一后端路径）。
type UiBuildType = 'precompiled' | Exclude<BuildType, 'package_neodata'>;

// 默认选中「使用编译好的版本」
const DEFAULT_BUILD_TYPE: UiBuildType = 'precompiled';
const BUILD_TYPE_LABELS: Record<UiBuildType, string> = {
  precompiled: '使用编译好的版本',
  package_bazel: '基于已有版本编译',
  compile_platform: 'cmake 编译（已废弃）',
};
const BUILD_TYPE_TOOLTIPS: Record<UiBuildType, string> = {
  precompiled: '直接使用已发布的算法包版本号或百度云 S3 包地址，无需重新编译。',
  package_bazel: '基于一个已有的基线版本 + 分支/commit 组合，走 cicd 平台重新编译一份产物。',
  compile_platform: '走仿真平台的 cmake 编译任务。该编译方式已经停止运维，后续出现问题将不再处理，望知晓！',
};
// 场景质检不提供 cmake（compile_platform）入口——该编译方式已废弃。
// compile_platform 分支代码在复制 Job 回填路径上仍保留，以兼容旧数据。
const BUILD_TYPE_ORDER: UiBuildType[] = [
  'precompiled',
  'package_bazel',
];

// precompiled 子选项卡：版本号（短串走后端模板）/ S3 地址（bos:// 完整路径透传）
type PrecompiledSubtype = 'version' | 's3';
const PRECOMPILED_SUBTYPE_LABELS: Record<PrecompiledSubtype, string> = {
  version: '版本号',
  s3: 'S3 地址',
};

// ── bazel 一键填版本预设 ──
// 预设 JSON 由后端 ``/job/bazel-presets`` 返回（管理员在「系统 - 系统参数运维」页
// 编辑）；前端挂载时拉取，不再硬编码。结构类型 BazelPreset / BazelPresets 在
// @apis/simulation 统一定义，本文件只 import BazelPresets 用。

// detectAlgoVersionPreset 统一收敛到 `@/utils/versionTag`（与 CreateJobModal 共用）。


// 「按场景 id 发起 job」单次最多携带的场景数（与后端 MAX_SCENE_IDS_PER_JOB 一致）。
export const MAX_SCENE_IDS_PER_JOB = 100;

// job.job_name 列为 varchar(64)，复制命名需与后端上限保持一致
const MAX_JOB_NAME_LEN = 64;

/**
 * 生成「复制」后的 Job 名称：把末尾连续的 `_copy` / `_copyN` 归一成单个带序号后缀，
 * 并截断 base 段保证不超过 {@link MAX_JOB_NAME_LEN}。
 */
function buildCopyName(name: string): string {
  if (!name) return '';
  const m = name.match(/^(.*?)((?:_copy\d*)+)$/);
  const base = m ? m[1] : name;
  let next = 1;
  if (m) {
    const last = m[2].match(/_copy(\d*)$/);
    next = last && last[1] ? parseInt(last[1], 10) + 1 : 2;
  }
  const suffix = next <= 1 ? '_copy' : `_copy${next}`;
  const maxBase = Math.max(0, MAX_JOB_NAME_LEN - suffix.length);
  const trimmedBase = base.length > maxBase ? base.slice(0, maxBase) : base;
  return `${trimmedBase}${suffix}`;
}

/** UI 端 module/branch 分隔符：兼容空格（首选）与冒号（兼容老格式）。 */
const BRANCH_DP_PAIR_SEP_RE = /[:\s]+/;
/** UI 端多对之间的分隔符：换行（首选）或英文逗号。 */
const BRANCH_DP_GROUP_SEP_RE = /[,\n]+/;

/** 把单行 ``module<sep>branch`` 拆成 ``[module, branch]``；不符合格式返回 null。 */
function parseBranchDpEntry(line: string): [string, string] | null {
  const trimmed = (line || '').trim();
  if (!trimmed) return null;
  const m = trimmed.split(BRANCH_DP_PAIR_SEP_RE);
  if (m.length < 2) return null;
  const module = m[0]?.trim();
  const branch = m.slice(1).join(' ').trim();
  if (!module || !branch) return null;
  return [module, branch];
}

/** 把 UI 多行输入归一化为后端接口契约的 ``module:branch,module2:branch2``。 */
function normalizeBranchDpInfoForApi(raw: string): string {
  return (raw || '')
    .split(BRANCH_DP_GROUP_SEP_RE)
    .map(parseBranchDpEntry)
    .filter((p): p is [string, string] => p !== null)
    .map(([module, branch]) => `${module}:${branch}`)
    .join(',');
}

/** 从 UI 输入提取模块名列表（用于 changeModule 自动填充）。 */
function deriveChangeModule(branchDpInfo: string): string {
  const modules: string[] = [];
  for (const entry of (branchDpInfo || '').split(BRANCH_DP_GROUP_SEP_RE)) {
    const pair = parseBranchDpEntry(entry);
    const mod = pair?.[0];
    if (mod && !modules.includes(mod)) {
      modules.push(mod);
    }
  }
  return modules.join(',');
}

/**
 * 合并「当前已填分支」与「一键填版本预设分支」，输出 ``module:branch,...`` 串。
 *
 * 规则（保序）：以当前已填条目为基准并保持其顺序，预设里**同名仓库覆盖**其分支、
 * **新仓库追加**到末尾；当前已填但预设未涉及的仓库**原样保留**。current 为空时结果
 * 等于预设本身（回退到旧「直接覆盖」行为）。
 */
function mergeBranchDpInfo(current: string, presetBranchDpInfo: string): string {
  const entries: [string, string][] = [];
  const posByModule = new Map<string, number>();
  const upsert = (raw: string) => {
    for (const line of (raw || '').split(BRANCH_DP_GROUP_SEP_RE)) {
      const pair = parseBranchDpEntry(line);
      if (!pair) continue;
      const [module, branch] = pair;
      const at = posByModule.get(module);
      if (at !== undefined) {
        entries[at] = [module, branch]; // 覆盖（同名去重 / 预设覆盖已填）
      } else {
        posByModule.set(module, entries.length);
        entries.push([module, branch]);
      }
    }
  };
  upsert(current);              // 先铺当前已填，确立顺序
  upsert(presetBranchDpInfo);   // 预设覆盖同名、追加新仓库
  // 用换行拼接、仓库与分支用空格间隔：UI 上一个仓库一行更易读
  // （parseBranchDpEntry 兼容空格/冒号，提交时 normalizeBranchDpInfoForApi 再统一成逗号串）。
  return entries.map(([m, b]) => `${m} ${b}`).join('\n');
}

const ALL_REPOS = ['cyber', 'common_neolix', 'runtime', 'cyberverse', 'dreamview', 'planning', 'tools', 'control', 'simulation'];
const DEFAULT_BRANCH = 'orin_dev';

function buildCommitIdString(input: string): string {
  const lines = input.split('\n').map(l => l.trim()).filter(l => l.length > 0);
  const repoMap = new Map<string, string>();
  for (const line of lines) {
    const parts = line.split(/\s+/);
    if (parts.length >= 2) {
      repoMap.set(parts[0], parts[1]);
    }
  }
  const segments = ALL_REPOS.map(repo => {
    const value = repoMap.get(repo) || DEFAULT_BRANCH;
    return `${repo}:${value}`;
  });
  return segments.join(',');
}

/** buildCommitIdString 的逆操作：把 ``repo:val,repo:val`` 还原为表单 textarea 文本。 */
function commitIdToTextarea(commitId: string | null | undefined): string {
  if (!commitId) return '';
  const lines: string[] = [];
  for (const pair of commitId.split(',')) {
    const [repo, value] = pair.split(':').map(x => x.trim());
    if (repo && value && value !== DEFAULT_BRANCH) {
      lines.push(`${repo} ${value}`);
    }
  }
  return lines.join('\n');
}

/** 把表单字段装配为 BuildSpec discriminated union。
 *  当前仅供 ``compile_platform``（cmake 编译已废弃）使用；
 *  ``precompiled`` 与 ``package_bazel`` 由 handleSubmit 直接组装。 */
function buildSpecFromForm(
  buildType: 'compile_platform',
  values: Record<string, any>,
): BuildSpec {
  if (buildType === 'compile_platform') {
    return {
      type: 'compile_platform',
      commitId: buildCommitIdString(values.branchCommitInput || ''),
    };
  }
  throw new Error(`buildSpecFromForm: unexpected buildType=${buildType as string}`);
}

interface CopyDefaults {
  algorithmVersion: string;
  /** 后端 snapshot 后的完整包路径（package_neodata 模板拼好 / bos:// 透传）。
   *  Radio 手动切到「导包」时取这个完整 URL 填 s3PackageUrl。 */
  fullPackageUrl: string;
  branchCommitInput: string;
  baseVersion?: string;
  versionDescription?: string;
  changeModule?: string;
  branchDpInfo?: string;
}

export interface PresetScene {
  id: number;
  name: string;
}

export interface CreateSceneQcJobModalProps {
  open: boolean;
  onClose: () => void;
  /** 创建成功后回调（如刷新列表）。 */
  onSuccess?: () => void;
  /**
   * 场景管理「发起任务」预选场景：调用方已持有 ``{id, name}``，弹窗内直接预填到
   * 「按场景」页签，**不再走 id→name 反查接口**。提交时使用 id 直发，上限
   * {@link MAX_SCENE_IDS_PER_JOB}。
   */
  presetScenes?: PresetScene[];
  /** 复制 job：传入原 record，弹窗打开时自动回填发起参数。 */
  copyRecord?: JobItem | null;
}

/**
 * 场景质检 · 创建 Job 弹窗（独立于 CreateJobModal，任务类型固定 SCENE_QC）。
 *
 * 与 CreateJobModal 的差异：
 * - 任务类型不可选：挂载时通过 `getSceneQcTaskType()` 拿 SCENE_QC 的 id，
 *   顶部只读展示「任务类型：场景质检 (SCENE_QC)」，提交时把 taskTypeId
 *   固定填成该 id。id 未拿到前 disable 「创建 Job」按钮。
 * - 其余场景来源 / buildType / bazel 编译等**保持一致**，用户明确要求
 *   「复制不复用」，不从 CreateJobModal 抽公共组件。
 */
export default function CreateSceneQcJobModal({
  open,
  onClose,
  onSuccess,
  presetScenes,
  copyRecord = null,
}: CreateSceneQcJobModalProps) {
  const [createForm] = Form.useForm();
  // SCENE_QC 任务类型 id（跨环境不同，挂载时向后端拉一次填 hidden field）。
  // 页面显示的"场景质检 (SCENE_QC)"是硬编码常量，不依赖此值。
  const [sceneQcTaskTypeId, setSceneQcTaskTypeId] = useState<number | null>(null);
  // 后台配置的场景生成 package_url 默认值（Modal 打开时拉一次）。
  // 存 state 后作为 <Form.Item initialValue> 传入，避免"条件渲染的 Form.Item
  // 尚未挂载时 setFieldsValue 被丢弃"的时序问题。
  const [defaultPackageUrl, setDefaultPackageUrl] = useState<string>('');
  // 默认 jobName（同上，走 initialValue 而不是 setFieldsValue，规避 destroyOnClose
  // 后 Form.Item 重挂载与 setFieldsValue 的 race。open 变化时重算 timestamp）。
  const [defaultJobName, setDefaultJobName] = useState<string>('');
  const [sceneTree, setSceneTree] = useState<SceneTreeNode[]>([]);
  const [createLoading, setCreateLoading] = useState(false);
  const [buildType, setBuildType] = useState<UiBuildType>(DEFAULT_BUILD_TYPE);
  // precompiled 内部子选项卡：version=版本号 / s3=S3 地址；两条都映射到后端 package_neodata。
  const [precompiledSubtype, setPrecompiledSubtype] = useState<PrecompiledSubtype>('version');
  // bazel 一键填版本 下拉当前选择：'custom' 表示自定义（不自动填），其它值为预设 key。
  // key 来自后端 /job/bazel-presets 返回的字典，运行时动态；不再硬编码 '10.10'/'10.9'。
  const [bazelPresetChoice, setBazelPresetChoice] = useState<'custom' | string>('custom');
  // bazel 预设字典：挂载时从 /job/bazel-presets 拉取；拉失败返回 {}，下拉只剩「自定义版本」。
  const [bazelPresets, setBazelPresets] = useState<BazelPresets>({});
  useEffect(() => {
    let cancelled = false;
    getJobBazelPresets()
      .then((presets) => {
        if (!cancelled) setBazelPresets(presets);
      })
      .catch((e) => {
        // 拉取失败按"不兜底"决策：下拉只剩自定义版本，侦测跳转静默禁用
        console.error('load bazel presets failed:', e);
      });
    return () => { cancelled = true; };
  }, []);

  // ── bazel 编译预设：一键填，复用同一份给「算法包版本侦测自动跳过来」 ──
  // baseVersion 优先级：外部传入 ``carryBaseVersion`` > preset 内置默认。
  // 两条路都会填 baseVersion，不再留空。
  const applyBazelPreset = useCallback((key: string, carryBaseVersion?: string) => {
    const preset = bazelPresets[key];
    if (!preset) {
      message.warning(`预设 ${key} 不存在或已下线，请重新选择`);
      return;
    }
    const carry = (carryBaseVersion || '').trim();
    const effectiveBaseVersion = carry || preset.baseVersion || '';
    // 合并而非覆盖：保留用户已填的仓库分支，预设仅覆盖同名仓库、补充新仓库。
    const currentBranchDpInfo = String(createForm.getFieldValue('branchDpInfo') || '');
    const mergedBranchDpInfo = mergeBranchDpInfo(currentBranchDpInfo, preset.branchDpInfo);
    const hadExisting = deriveChangeModule(currentBranchDpInfo).length > 0;
    const patch: Record<string, unknown> = {
      branchDpInfo: mergedBranchDpInfo,
      changeModule: deriveChangeModule(mergedBranchDpInfo),
    };
    if (effectiveBaseVersion) {
      patch.baseVersion = effectiveBaseVersion;
    }
    createForm.setFieldsValue(patch);
    const mergeHint = hadExisting ? '（已合并你已填的分支，同名仓库以预设为准）' : '';
    message.success(
      carry
        ? `已填入 ${preset.label} 预设分支并带入算法版本到基线${mergeHint}`
        : `已填入 ${preset.label} 预设分支与默认基线版本${mergeHint}`,
    );
  }, [createForm, bazelPresets]);

  // 算法包版本输入侦测：命中任一预设 key 时提示跳 bazel 编译并自动填预设
  const handleAlgoVersionBlur = useCallback((raw: string) => {
    const ver = detectAlgoVersionPreset(raw, Object.keys(bazelPresets));
    if (!ver) return;
    Modal.confirm({
      title: `检测到算法版本 ${ver}`,
      content: `${ver} 算法包存在仿真代码不全的问题，建议改走「bazel 编译」重新编译一份。是否跳转到 bazel 编译并自动填入 ${ver} 预设？基线版本会自动带入当前算法版本。`,
      okText: `跳转 bazel 编译并填 ${ver}`,
      cancelText: '保持算法包版本',
      onOk: () => {
        setBuildType('package_bazel');
        // 同步下拉状态：从 handleAlgoVersionBlur 触发的跳转选中对应版本；
        // 让「一键填版本」下拉与实际填入的字段保持一致，不再显示为「自定义版本」。
        setBazelPresetChoice(ver);
        // 等下一帧渲染出 bazel 块再 setFieldsValue
        setTimeout(() => applyBazelPreset(ver, raw), 0);
      },
    });
  }, [applyBazelPreset, bazelPresets]);
  const [copyDefaults, setCopyDefaults] = useState<CopyDefaults | null>(null);
  // 场景来源页签（仅无预选场景时可切换）：'sceneSet'=按场景集；'scene'=按场景名输入
  const [sceneTab, setSceneTab] = useState<'sceneSet' | 'scene'>('sceneSet');
  // 复制「按场景」job 时带入的原始场景 id：未改动场景名输入则按此 id 直接提交，
  // 避免「id→名→id」反查链路出错；用户一旦编辑场景名则置空、回退为按名反查。
  const [copiedSceneIds, setCopiedSceneIds] = useState<number[] | null>(null);

  // 场景管理「发起任务」带入的场景列表（id+name 稳定串作依赖，避免数组身份变化导致 effect 重跑）
  const presetSceneKey = (presetScenes || []).map((s) => `${s.id}:${s.name}`).join('|');
  // effect 内部按需读取最新 presetScenes，但不让它进 deps（数组身份不稳定，已用 presetSceneKey 跟踪）
  const presetScenesRef = useRef(presetScenes);
  presetScenesRef.current = presetScenes;

  // 挂载后拉一次 SCENE_QC 的 id 塞进 hidden taskTypeId。seed 未跑 / 网络失败时
  // id 为 null，提交时会兜底再拉一次并 fail-fast（避免展示层出现 error 文案）。
  const loadSceneQcTaskType = useCallback(async () => {
    const id = await getSceneQcTaskTypeId();
    setSceneQcTaskTypeId(id);
    if (id != null) {
      createForm.setFieldsValue({ taskTypeId: id });
    }
  }, [createForm]);

  // 「按场景集合」页签需要场景树
  const loadSceneTree = useCallback(async () => {
    try {
      const tree = await getSceneTree(true);
      setSceneTree(tree);
    } catch (err) {
      console.error('加载场景树失败:', err);
    }
  }, []);

  // 按场景 id 预填「按场景」页签（复制 job 用）：调用方只有 id，本地无 name，需反查 scene_inspection。
  // 直查不受场景树归属限制；按场景发起的场景常不在任何场景集树下。
  const prefillSceneTabByIds = useCallback(async (ids: number[]) => {
    setSceneTab('scene');
    setCopiedSceneIds(ids.length > 0 ? ids : null);
    let sceneNames: string[] = [];
    if (ids.length > 0) {
      try {
        const res = await getSceneInspectionList({ ids, current: 1, pageSize: ids.length });
        sceneNames = (res.list || []).map((s) => s.name).filter(Boolean);
      } catch (err) {
        console.error('场景 id 反查场景名失败:', err);
        message.warning('场景名解析失败，请手动确认场景名');
      }
    }
    createForm.setFieldsValue({ sceneNames });
  }, [createForm]);

  // 场景管理「发起任务」预填：调用方已带 id+name，直接填，不调接口。
  const prefillSceneTabByScenes = useCallback((scenes: PresetScene[]) => {
    setSceneTab('scene');
    setCopiedSceneIds(scenes.length > 0 ? scenes.map((s) => s.id) : null);
    createForm.setFieldsValue({
      sceneNames: scenes.map((s) => s.name).filter(Boolean),
    });
  }, [createForm]);

  // 把原 job 的发起参数回填进表单（复制 job）。
  // 场景来源按原 job 的 sceneSource 区分回填到对应页签：
  // - scene_set：场景集 id 回填 TreeSelect；
  // - scene：sceneSetIds 实为场景 id 串，反查场景名回填「按场景」页签（不复用 TreeSelect）。
  const applyCopyRecord = useCallback(async (record: JobItem) => {
    let packageVersion = '';
    let commitInput = '';
    const bz = record.bazelInputs || {};
    const baseVersion = (bz.base_version || '').trim();
    const versionDescription = bz.version_description ?? 'test';
    const changeModule = bz.change_module || '';
    const branchDpInfo = (bz.branch_dp_info || '')
      .split(',')
      .map((entry: string) => entry.trim())
      .filter(Boolean)
      .map((entry: string) => entry.replace(/:/, ' '))
      .join('\n');

    // record.algorithmVersion 是后端 snapshot 后的「完整包路径」：
    // - package_neodata 短版本号 → 走模板拼好的完整 URL
    // - 用户原填 bos:///obs:// 全路径 → 透传保持原样
    // - bazel 编译 → bazel 自动生成的 version snapshot
    // 因此判定「是否 bos:// 全路径」必须看 snapshot 字段，而不是 buildSpec 里用户原输入。
    const fullPackageUrl = (record.algorithmVersion || '').trim();
    if (record.buildType === 'compile_platform') {
      packageVersion = fullPackageUrl;
      if (record.buildSpec?.type === 'compile_platform') {
        commitInput = commitIdToTextarea(record.buildSpec.commitId);
      }
    } else {
      packageVersion =
        (record.buildSpec?.type === 'package_neodata' || record.buildSpec?.type === 'package_bazel') &&
        record.buildSpec.algorithmVersion
          ? record.buildSpec.algorithmVersion
          : fullPackageUrl;
    }

    setCopyDefaults({
      algorithmVersion: packageVersion,
      fullPackageUrl,
      branchCommitInput: commitInput,
      baseVersion,
      versionDescription,
      changeModule,
      branchDpInfo,
    });
    // UI 类型判定：
    // - package_bazel / compile_platform 走各自分支
    // - 其余合并到 precompiled；再按 snapshot 全路径是否 bos:// 决定子选项卡
    let initialUiType: UiBuildType;
    let initialPrecompiledSub: PrecompiledSubtype = 'version';
    if (record.buildType === 'package_bazel') {
      initialUiType = 'package_bazel';
    } else if (record.buildType === 'compile_platform') {
      initialUiType = 'compile_platform';
    } else {
      initialUiType = 'precompiled';
      initialPrecompiledSub = /^bos:\/\//i.test(fullPackageUrl) ? 's3' : 'version';
    }
    setBuildType(initialUiType);
    setPrecompiledSubtype(initialPrecompiledSub);
    // 复制场景下的 bazel 一键填选择：Job 是已知版本时用户能自主选，
    // 这里统一回到「自定义版本」，避免覆盖用户复制过来的原始 baseVersion/branchDpInfo。
    setBazelPresetChoice('custom');

    const isS3 = initialUiType === 'precompiled' && initialPrecompiledSub === 's3';
    // 复制到本 Modal 时 taskTypeId 强制固定 SCENE_QC，不沿用原 record（可能是其他任务类型的 job）
    const baseFields = {
      taskTypeId: sceneQcTaskTypeId ?? record.taskTypeId,
      priority: record.priority,
      jobName: record.jobName ? buildCopyName(record.jobName) : undefined,
      // 「S3 地址」子选项卡填 snapshot 全路径；「版本号」子选项卡填原短版本号。
      algorithmVersion: isS3 ? undefined : packageVersion,
      s3PackageUrl: isS3 ? fullPackageUrl : undefined,
      branchCommitInput: commitInput,
      baseVersion,
      versionDescription,
      changeModule,
      branchDpInfo,
    };

    const rawSceneTokens = (record.sceneSetIds || '')
      .split(',')
      .map((s) => s.trim())
      .filter(Boolean);

    if (record.sceneSource === 'scene') {
      // 按场景发起的 job：sceneSetIds 为场景 id 串，回填「按场景」页签（带原始 id + 反查名）
      createForm.setFieldsValue(baseFields);
      const ids = rawSceneTokens.map(Number).filter((n) => !Number.isNaN(n));
      await prefillSceneTabByIds(ids);
    } else {
      setSceneTab('sceneSet');
      const sceneSetIds = rawSceneTokens
        .map(Number)
        .filter((n) => !Number.isNaN(n));
      createForm.setFieldsValue({ ...baseFields, sceneSetIds });
    }
  }, [createForm, prefillSceneTabByIds, sceneQcTaskTypeId]);

  // 弹窗打开：重置 → 加载任务类型（非预选模式加载场景树）→ 复制回填
  useEffect(() => {
    if (!open) return;
    let cancelled = false;
    setCopyDefaults(null);
    setCopiedSceneIds(null);
    setBuildType(copyRecord?.buildType === 'package_bazel' ? 'package_bazel' : DEFAULT_BUILD_TYPE);
    setPrecompiledSubtype('version');
    setBazelPresetChoice('custom');
    setSceneTab('sceneSet');
    createForm.resetFields();
    // 打开即清一次上次残留（关一次开一次时 defaultPackageUrl 会被下一次异步覆盖）
    setDefaultPackageUrl('');
    (async () => {
      // 必须先 await loadSceneQcTaskType（会把 taskTypeId 写入表单），再做回填，
      // 否则带入的 taskTypeId 会被随后到达的默认值覆盖。两种页签都可能用到场景树。
      const [, , url] = await Promise.all([
        loadSceneQcTaskType(),
        loadSceneTree(),
        getSceneQcDefaultPackageUrl(),
      ]);
      if (cancelled) return;
      if (copyRecord) {
        await applyCopyRecord(copyRecord);
      } else if (presetScenesRef.current && presetScenesRef.current.length > 0) {
        // 场景管理「发起任务」：调用方已持有 id+name，直接预填，不再走反查接口
        prefillSceneTabByScenes(presetScenesRef.current);
      } else if (url) {
        // 非复制、非预选场景（纯新建）：把后台配置的 generate-scene package_url
        // 存 state 后作为 <Form.Item initialValue> 传入，避免"条件渲染的
        // Form.Item 尚未挂载时 setFieldsValue 被丢弃"的时序问题；
        // bos:// 前缀切到「S3 地址」子选项卡（同一 state 作为 s3PackageUrl 初始值）。
        setDefaultPackageUrl(url);
        if (/^bos:\/\//i.test(url)) {
          setPrecompiledSubtype('s3');
        }
      }
    })();
    return () => { cancelled = true; };
  }, [
    open, copyRecord, presetSceneKey,
    loadSceneQcTaskType, loadSceneTree, applyCopyRecord, prefillSceneTabByScenes, createForm,
  ]);

  // 打开弹窗即生成默认任务名（复制/预选带入的场景保持原名，走 copyDefaults 分支覆盖）；
  // 存 state 后由 <Form.Item initialValue> 应用，配合 key 触发重挂载，
  // 避免 setFieldsValue 在 destroyOnClose 场景下被丢弃。
  useEffect(() => {
    if (!open) return;
    if (copyDefaults) return; // 复制场景保留原 jobName
    const now = new Date();
    const timestamp = [
      now.getFullYear(),
      String(now.getMonth() + 1).padStart(2, '0'),
      String(now.getDate()).padStart(2, '0'),
      '_',
      String(now.getHours()).padStart(2, '0'),
      String(now.getMinutes()).padStart(2, '0'),
      String(now.getSeconds()).padStart(2, '0'),
      '_',
      String(now.getMilliseconds()).padStart(3, '0'),
    ].join('');
    setDefaultJobName(`${SCENE_QC_TASK_TYPE_NAME}_${timestamp}`);
  }, [open, copyDefaults]);

  // 将场景树转换为 TreeSelect 格式，剔除 is_hidden=true 的节点及其后代
  const convertSceneTreeToTreeData = (nodes: SceneTreeNode[]): any[] => {
    return nodes
      .filter((node) => !node.isHidden)
      .map((node) => ({
        title: node.name,
        value: node.id,
        key: node.id,
        children: node.children && node.children.length > 0
          ? convertSceneTreeToTreeData(node.children)
          : undefined,
      }));
  };

  const handleCancel = () => {
    setCopyDefaults(null);
    createForm.resetFields();
    onClose();
  };

  /**
   * 把用户输入的场景名精确反查为场景 id（「按场景·输入场景名」提交前调用）。
   * 走 ``POST /scene/inspection/resolve-ids-by-names``，传 ``requireCompleted=false``
   * —— **场景质检任务接受任意状态的场景**（质检中 / 已录入 / 不合格都可，质检本就是
   * 去跑质检）；命中未找到 / 已删除 / 无权限时后端抛错、axios 拦截器自动 message.error，
   * 本函数返 null 终止任务创建流程。
   */
  const resolveSceneIdsByNames = (names: string[]): Promise<number[] | null> =>
    resolveSceneIdsByNamesApi(names, false);

  const handleSubmit = async () => {
    try {
      const values = await createForm.validateFields();

      setCreateLoading(true);

      // 计算场景来源载荷（统一归到按场景集 sceneSetIds 或按场景 sceneIds）：
      // - sceneTab='scene'：带入的原始 id（复制/预选未改动）直接用，否则按输入场景名反查 id；
      // - sceneTab='sceneSet'：TreeSelect 多选的 sceneSetIds 逗号串。
      let sceneSourcePayload: { sceneIds?: number[]; sceneSetIds?: string };
      if (sceneTab === 'scene') {
        // 复制/预选带入且未改动场景名：直接用原始场景 id（不依赖场景名反查链路）
        if (copiedSceneIds && copiedSceneIds.length > 0) {
          if (copiedSceneIds.length > MAX_SCENE_IDS_PER_JOB) {
            message.error(`按场景发起最多 ${MAX_SCENE_IDS_PER_JOB} 个场景，当前 ${copiedSceneIds.length} 个`);
            return;
          }
          sceneSourcePayload = { sceneIds: copiedSceneIds };
        } else {
          const names: string[] = Array.from(
            new Set(
              ((values.sceneNames as string[]) || [])
                .map((n) => (n || '').trim())
                .filter(Boolean),
            ),
          );
          if (names.length === 0) {
            message.error('请输入场景名称');
            return;
          }
          if (names.length > MAX_SCENE_IDS_PER_JOB) {
            message.error(`按场景发起最多 ${MAX_SCENE_IDS_PER_JOB} 个场景，当前 ${names.length} 个`);
            return;
          }
          const resolvedIds = await resolveSceneIdsByNames(names);
          if (!resolvedIds) {
            return; // 反查失败 / 有不存在的场景名，错误已提示
          }
          sceneSourcePayload = { sceneIds: resolvedIds };
        }
      } else {
        const sceneSetIdsArray = Array.isArray(values.sceneSetIds)
          ? values.sceneSetIds.map((id: number | string) => String(id))
          : [];
        sceneSourcePayload = { sceneSetIds: sceneSetIdsArray.join(',') };
      }

      // 高级选项：重复运行次数 1-10，默认 1。仅 N>1 时透传给后端走循环创建。
      const repeatTimes = Math.max(1, Math.min(10, Number(values.repeatTimes) || 1));
      const repeatSuffix = repeatTimes > 1 ? `（共 ${repeatTimes} 个）` : '';

      if (buildType === 'package_bazel') {
        const baseVersion = (values.baseVersion || '').trim();
        if (!baseVersion) {
          message.error('请输入基线版本');
          setCreateLoading(false);
          return;
        }
        const bazelParams: CreateBazelJobParams = {
          taskTypeId: Number(values.taskTypeId),
          jobName: values.jobName || undefined,
          priority: values.priority !== undefined ? Number(values.priority) : undefined,
          ...sceneSourcePayload,
          baseVersion,
          versionDescription:
            (values.versionDescription || '').trim() ||
            `${SCENE_QC_TASK_TYPE_NAME}仿真`,
          changeModule: (values.changeModule || '').trim(),
          branchDpInfo: normalizeBranchDpInfoForApi(values.branchDpInfo || ''),
          ...(repeatTimes > 1 ? { repeatTimes } : {}),
          // 「一键填入」下拉选到具体预设时（如 10.10）把 key 传给后端拼进 version
          // 号最前面；选「自定义版本」时 bazelPresetChoice='custom'，不传。
          ...(bazelPresetChoice !== 'custom' ? { presetKey: bazelPresetChoice } : {}),
          // 运行模式：透传到每个 dataset.params.mode，下游 DAG 按 mode 分流。
          params: { mode: values.runMode || 'qc_check' },
        };
        const result = await createBazelJob(bazelParams);
        message.success(
          `已发起 bazel 编译${repeatSuffix}（版本 ${result.version}），编译完成后自动调度`,
        );
      } else {
        // precompiled 是前端 UI 合并（版本号 + S3 地址），都映射到后端 package_neodata。
        // 后端 PackageUrlResolver 对 bos:// / obs:// 完整路径透传不套模板，对短版本号套模板。
        let resolvedBuildType: BuildType;
        let buildSpec: BuildSpec;
        if (buildType === 'precompiled') {
          if (precompiledSubtype === 's3') {
            const bosUrl = (values.s3PackageUrl || '').trim();
            if (!/^bos:\/\//i.test(bosUrl)) {
              message.error('S3 地址必须以 bos:// 开头');
              setCreateLoading(false);
              return;
            }
            resolvedBuildType = 'package_neodata';
            buildSpec = { type: 'package_neodata', algorithmVersion: bosUrl };
          } else {
            const ver = (values.algorithmVersion || '').trim();
            if (!ver) {
              message.error('请输入版本号');
              setCreateLoading(false);
              return;
            }
            resolvedBuildType = 'package_neodata';
            buildSpec = { type: 'package_neodata', algorithmVersion: ver };
          }
        } else {
          // 剩 compile_platform（已废弃保留兼容）
          resolvedBuildType = buildType as 'compile_platform';
          buildSpec = buildSpecFromForm(resolvedBuildType, values);
        }
        const params: CreateJobParams = {
          priority: Number(values.priority),
          taskTypeId: Number(values.taskTypeId),
          jobName: values.jobName || undefined,
          ...sceneSourcePayload,
          buildType: resolvedBuildType,
          buildSpec,
          ...(repeatTimes > 1 ? { repeatTimes } : {}),
          // 运行模式：透传到每个 dataset.params.mode，下游 DAG 按 mode 分流。
          params: { mode: values.runMode || 'qc_check' },
        };
        await createJob(params);
        message.success(`Job 创建成功${repeatSuffix}`);
      }
      setCopyDefaults(null);
      createForm.resetFields();
      onClose();
      onSuccess?.();
    } catch (err: any) {
      if (err?.errorFields) {
        return; // 表单校验错误
      }
      console.error('创建任务失败:', err);
    } finally {
      setCreateLoading(false);
    }
  };

  return (
    <Modal
      title="创建场景质检 Job"
      open={open}
      onCancel={handleCancel}
      footer={[
        <Button key="cancel" onClick={handleCancel}>取消</Button>,
        <Button
          key="submit"
          type="primary"
          loading={createLoading}
          onClick={handleSubmit}
        >
          创建 Job
        </Button>,
      ]}
      width={600}
      destroyOnHidden
    >
      <Form
        form={createForm}
        layout="vertical"
        style={{ marginTop: 20 }}
        onValuesChange={(changed) => {
          // 用户改动了场景名输入 → 放弃复制带入的原始场景 id，改走按名反查
          if ('sceneNames' in changed && copiedSceneIds) {
            setCopiedSceneIds(null);
          }
        }}
      >
        {/* 任务类型固定为 SCENE_QC：与 CreateJobModal 的「任务类型」Select 视觉对齐，
            改成只读 Input（不给自造样式，避免出现黑底违和感）；hidden field 承接提交值。 */}
        <Form.Item label="任务类型">
          <Input value={SCENE_QC_TASK_TYPE_LABEL} readOnly disabled />
        </Form.Item>
        {/* 用 key 依赖 sceneQcTaskTypeId 强制在异步拉到 id 后重挂载并应用 initialValue；
            单纯 setFieldsValue 存在 Form.Item 尚未挂载时被丢弃的时序 bug（destroyOnClose 加重）。 */}
        <Form.Item
          key={`taskTypeId-${sceneQcTaskTypeId ?? 'null'}`}
          name="taskTypeId"
          hidden
          initialValue={sceneQcTaskTypeId ?? undefined}
        >
          <InputNumber />
        </Form.Item>

        <Form.Item
          label="创建类型"
          required
          tooltip={
            <div style={{ whiteSpace: 'pre-line' }}>
              {`使用编译好的版本：${BUILD_TYPE_TOOLTIPS.precompiled}\n基于已有版本编译：${BUILD_TYPE_TOOLTIPS.package_bazel}\ncmake 编译（已废弃）：${BUILD_TYPE_TOOLTIPS.compile_platform}`}
            </div>
          }
        >
          <Radio.Group
            value={buildType}
            onChange={(e) => {
              const next = e.target.value as UiBuildType;
              setBuildType(next);
              if (copyDefaults) {
                if (next === 'compile_platform') {
                  createForm.setFieldsValue({
                    branchCommitInput: copyDefaults.branchCommitInput,
                    algorithmVersion: undefined,
                    s3PackageUrl: undefined,
                  });
                } else if (next === 'package_bazel') {
                  createForm.setFieldsValue({
                    baseVersion: copyDefaults.baseVersion,
                    versionDescription: copyDefaults.versionDescription || undefined,
                    changeModule: copyDefaults.changeModule,
                    branchDpInfo: copyDefaults.branchDpInfo,
                    algorithmVersion: undefined,
                    branchCommitInput: undefined,
                    s3PackageUrl: undefined,
                  });
                } else if (next === 'precompiled') {
                  // snapshot 完整路径是 bos:// → 落到「S3 地址」子选项卡并带入 URL；
                  // 否则落到「版本号」子选项卡并回填原短版本号。
                  const full = (copyDefaults.fullPackageUrl || '').trim();
                  const isBos = /^bos:\/\//i.test(full);
                  setPrecompiledSubtype(isBos ? 's3' : 'version');
                  createForm.setFieldsValue({
                    algorithmVersion: isBos ? undefined : copyDefaults.algorithmVersion,
                    s3PackageUrl: isBos ? full : undefined,
                    branchCommitInput: undefined,
                  });
                }
              } else {
                createForm.setFieldsValue({
                  algorithmVersion: undefined,
                  branchCommitInput: undefined,
                  baseVersion: undefined,
                  changeModule: undefined,
                  branchDpInfo: undefined,
                  versionDescription: undefined,
                  s3PackageUrl: undefined,
                });
              }
            }}
          >
            {BUILD_TYPE_ORDER.map((bt) => {
              // cmake 编译已废弃：label 文字置灰提示，但仍保留可选（兼容旧数据复制）
              const deprecated = bt === 'compile_platform';
              return (
                <Tooltip key={bt} title={BUILD_TYPE_TOOLTIPS[bt]} placement="top">
                  <Radio value={bt}>
                    <span style={deprecated ? { color: '#9b9da3' } : undefined}>
                      {BUILD_TYPE_LABELS[bt]}
                    </span>
                  </Radio>
                </Tooltip>
              );
            })}
          </Radio.Group>
        </Form.Item>

        {buildType === 'precompiled' && (
          <>
            {/* 子选项卡：版本号 / S3 地址 —— 提交时都走后端 package_neodata */}
            <Form.Item label="输入方式" tooltip={BUILD_TYPE_TOOLTIPS.precompiled}>
              <Segmented
                value={precompiledSubtype}
                onChange={(v) => {
                  const next = v as PrecompiledSubtype;
                  setPrecompiledSubtype(next);
                  // 切换子选项卡清掉另一侧的字段，避免残留误提交
                  if (next === 'version') {
                    createForm.setFieldsValue({ s3PackageUrl: undefined });
                  } else {
                    createForm.setFieldsValue({ algorithmVersion: undefined });
                  }
                }}
                options={[
                  { label: PRECOMPILED_SUBTYPE_LABELS.version, value: 'version' },
                  { label: PRECOMPILED_SUBTYPE_LABELS.s3, value: 's3' },
                ]}
              />
            </Form.Item>

            {precompiledSubtype === 'version' && (
              <Form.Item
                // key 依赖默认值 → 异步拉到默认包后 Item 重挂载，initialValue 才生效
                // （initialValue 只在挂载首帧应用；不加 key 时会被先前的空 initialValue 卡住）。
                key={`algoVer-${defaultPackageUrl}`}
                name="algorithmVersion"
                label="版本号"
                // 后台配置的 generate-scene package_url 作为默认值：非 bos:// 前缀走
                // 版本号子选项卡；条件渲染 → 用 initialValue 才不会被 setFieldsValue race。
                initialValue={/^bos:\/\//i.test(defaultPackageUrl) ? undefined : defaultPackageUrl}
                rules={[{ required: true, message: '请输入版本号' }]}
                tooltip="填写已发布的算法包版本号（如 x86.20260430.release.10.8.3），后端按模板拼装 OBS 地址。"
              >
                <Input
                  placeholder="请输入版本号（如 x86.20260430.release.10.8.3）"
                  onBlur={(e) => handleAlgoVersionBlur(e.target.value)}
                />
              </Form.Item>
            )}

            {precompiledSubtype === 's3' && (
              <Form.Item
                // 同上，key 依赖默认值以在异步拉到 bos:// 默认值时重挂载 Item 应用 initialValue。
                key={`s3Pkg-${defaultPackageUrl}`}
                name="s3PackageUrl"
                label="S3 地址"
                // bos:// 前缀的默认值走此子选项卡；同样用 initialValue 规避条件渲染时序问题。
                initialValue={/^bos:\/\//i.test(defaultPackageUrl) ? defaultPackageUrl : undefined}
                tooltip="导入百度云的包，仅支持 bos:// 开头的地址。"
                rules={[
                  { required: true, message: '请输入 S3 地址' },
                  {
                    validator: (_, value) => {
                      const v = (value || '').trim();
                      if (!v) return Promise.resolve();
                      if (!/^bos:\/\//i.test(v)) {
                        return Promise.reject(new Error('S3 地址必须以 bos:// 开头（仅支持百度云）'));
                      }
                      return Promise.resolve();
                    },
                  },
                ]}
                extra={<span style={{ fontSize: 12, color: '#999' }}>导入百度云的包，仅支持 bos:// 开头的地址</span>}
              >
                <Input placeholder="bos://bucket/path/to/package.tgz" />
              </Form.Item>
            )}
          </>
        )}

        {buildType === 'compile_platform' && (
          <Form.Item
            name="branchCommitInput"
            label="分支 & Commit ID"
            tooltip={BUILD_TYPE_TOOLTIPS.compile_platform}
            extra={
              <span style={{ fontSize: 12, color: '#999' }}>
                每行格式：仓库名 分支名/CommitID,如：planning orin_dev
                <br />
                支持仓库：{ALL_REPOS.join('、')}
                <br />
                留空则所有仓库默认使用 orin_dev 分支
              </span>
            }
          >
            <Input.TextArea
              placeholder={`planning orin_dev\ncontrol db31cf58ef237a576eab0d6e407b18fc80d29286`}
              autoSize={{ minRows: 3, maxRows: 10 }}
            />
          </Form.Item>
        )}

        {buildType === 'package_bazel' && (
          <>
            <Form.Item
              label="一键填版本"
              tooltip="选择任一预设，把对应版本的 neolix-repo / cyber / runtime / tools / dreamview / simulation 分支组合与默认基线版本合并到「分支信息」（同名仓库以预设为准、其余保留）；「自定义版本」不主动填充，但保留你已填的内容。预设由管理员在「系统 - 系统参数运维」页维护。"
            >
              <Select
                value={bazelPresetChoice}
                style={{ width: 200 }}
                // 用 onSelect 而非 onChange：onChange 仅在值变化时触发，重复选同一预设不会有动作；
                // onSelect 每次点选项都触发（含再次选中当前项），满足「选相同版本重新填充」。
                onSelect={(v: 'custom' | string) => {
                  setBazelPresetChoice(v);
                  // 切到「自定义版本」时保留已填的基线/分支，作为手动微调的起点，不再清空
                  // （提交时 custom 仅表示不带 presetKey，字段仍从表单取）。
                  if (v !== 'custom') {
                    applyBazelPreset(v);
                  }
                }}
                options={[
                  { label: '自定义版本', value: 'custom' },
                  ...Object.entries(bazelPresets).map(([key, p]) => ({
                    label: p.label || key,
                    value: key,
                  })),
                ]}
              />
              <span style={{ marginLeft: 8, fontSize: 12, color: '#9b9da3' }}>
                选预设会合并到下方「分支信息」（同名仓库以预设为准、其余保留），并填入「基线版本」
              </span>
            </Form.Item>

            <Form.Item
              name="baseVersion"
              label="基线版本"
              rules={[
                { required: true, message: '请输入基线版本' },
                {
                  validator: async (_, value) => {
                    if (typeof value === 'string' && value.trim().length > 0) {
                      return;
                    }
                    throw new Error('请输入基线版本');
                  },
                },
              ]}
              tooltip="本次编译所基于的基线版本（base_version，必填）；编译产物版本号由后端按「日期.simulation.{环境}.{用户ID}.序号」自动生成。"
              extra={
                <span style={{ color: '#9b9da3', fontSize: 12 }}>
                  必填，建议填写具体基线 tag（如 20260609.release.10.10.8）。
                </span>
              }
            >
              <Input placeholder="如 20260609.release.10.10.8" />
            </Form.Item>

            <Form.Item
              name="branchDpInfo"
              label="分支信息"
              extra={
                <span style={{ fontSize: 12, color: '#999' }}>
                  每行一对，格式：<code>module branch</code>（空格分隔）；
                  也兼容老格式 <code>module:branch</code>。提交时自动规整为
                  <code>module:branch,...</code> 发给后端。
                  <br />
                  分支位置也可直接填 <code>40 位 commitId</code>，
                  系统识别为 commit 直接使用、不再解析分支；<strong>短 commit 不支持</strong>。
                  <br />
                  仓库不变（无分支改动）则留空
                </span>
              }
              getValueFromEvent={(e) => {
                const v = e.target.value;
                createForm.setFieldsValue({ changeModule: deriveChangeModule(v) });
                return v;
              }}
            >
              <Input.TextArea
                placeholder={`planning orin_dev\ncontrol db31cf58ef237a576eab0d6e407b18fc80d29286`}
                autoSize={{ minRows: 2, maxRows: 8 }}
              />
            </Form.Item>

            {/* changeModule / versionDescription 不再展示为独立字段：
                - changeModule 由「分支信息」输入时的 deriveChangeModule 自动写入 form 值；
                - versionDescription 留空时 handleSubmit 会兜底按任务类型拼 "{taskType}仿真"。
                两个字段仍作为 Form 隐藏项存在，提交时 values.changeModule /
                values.versionDescription 正常读到，接口参数不变。 */}
            <Form.Item name="changeModule" hidden>
              <Input />
            </Form.Item>
            <Form.Item name="versionDescription" hidden>
              <Input />
            </Form.Item>
          </>
        )}

        <Form.Item
          // key 依赖 defaultJobName → 每次 open 变化重算 timestamp 后 Item 重挂载并
          // 应用最新 initialValue，避免 setFieldsValue 在 destroyOnClose 场景下被丢弃。
          key={`jobName-${defaultJobName}`}
          name="jobName"
          label="Job 名称"
          initialValue={defaultJobName || undefined}
          rules={[{ required: true, message: '请输入 Job 名称' }]}
        >
          <Input placeholder="请输入 Job 名称" />
        </Form.Item>

        {/* 运行模式：Radio 二选一，透传给 dataset.params.mode 供下游 DAG 分流。
            默认「仅质检」（mode=qc_check）—— 复用已有生成结果只跑质检环节；
            「更新且质检」（mode=generate_and_qc_check）—— 先重新生成再走质检。 */}
        <Form.Item
          name="runMode"
          label="运行模式"
          initialValue="qc_check"
          tooltip="仅质检：复用已生成结果，只跑质检评测；更新且质检：先重新生成场景，再走质检评测。"
        >
          <Radio.Group>
            <Radio value="qc_check">仅质检</Radio>
            <Radio value="generate_and_qc_check">更新且质检</Radio>
          </Radio.Group>
        </Form.Item>

        {/* 场景来源：「按场景集合 / 按场景」二选一页签，按场景为输入场景名。
            场景管理「发起任务」带入的场景 id 由 prefillSceneTabByIds 反查后填入「按场景」输入框。 */}
        <Form.Item label="场景来源" required>
          <Segmented
            value={sceneTab}
            onChange={(v) => setSceneTab(v as 'sceneSet' | 'scene')}
            options={[
              { label: '按场景集合', value: 'sceneSet' },
              { label: '按场景', value: 'scene' },
            ]}
          />
        </Form.Item>

        {sceneTab === 'sceneSet' && (
          <Form.Item
            name="sceneSetIds"
            label="场景列表"
            required
            rules={[
              {
                required: true,
                validator: (_, value) =>
                  Array.isArray(value) && value.length > 0
                    ? Promise.resolve()
                    : Promise.reject(new Error('请至少选择一个场景集合')),
              },
            ]}
          >
            <TreeSelect
              treeData={convertSceneTreeToTreeData(sceneTree)}
              placeholder="请选择场景集合（可多选）"
              multiple
              treeCheckable
              showCheckedStrategy="SHOW_PARENT"
              showSearch
              filterTreeNode={(inputValue, treeNode) => {
                const title = treeNode?.title as string;
                return title?.toLowerCase().includes(inputValue.toLowerCase());
              }}
              style={{ width: '100%' }}
            />
          </Form.Item>
        )}

        {sceneTab === 'scene' && (
          <Form.Item
            name="sceneNames"
            label="场景名称"
            required
            rules={[
              {
                required: true,
                validator: (_, value) => {
                  const names = Array.isArray(value)
                    ? value.map((n: string) => (n || '').trim()).filter(Boolean)
                    : [];
                  if (names.length === 0) {
                    return Promise.reject(new Error('请输入场景名称'));
                  }
                  if (names.length > MAX_SCENE_IDS_PER_JOB) {
                    return Promise.reject(
                      new Error(`最多 ${MAX_SCENE_IDS_PER_JOB} 个场景，当前 ${names.length} 个`),
                    );
                  }
                  return Promise.resolve();
                },
              },
            ]}
            extra={
              <span style={{ fontSize: 12, color: '#999' }}>
                输入场景名称后回车确认，可输入多个（支持逗号 / 空格 / 换行分隔粘贴），
                上限 {MAX_SCENE_IDS_PER_JOB} 个
              </span>
            }
          >
            <Select
              mode="tags"
              open={false}
              tokenSeparators={[',', '，', ' ', ';', '；', '\n', '\t']}
              placeholder="请输入场景名称，如 LPL0000291017"
              style={{ width: '100%' }}
            />
          </Form.Item>
        )}

        {/* 高级选项：默认收起。当前只放「重复运行次数」，后续如需扩展再添字段。
            选择 Collapse ghost 让面板与 Form 视觉不脱节，避免在弹窗内出现重边框感。 */}
        <Collapse
          ghost
          size="small"
          items={[{
            key: 'advanced',
            label: <span style={{ fontSize: 13, color: '#5e6ad2' }}>高级选项</span>,
            children: (
              <Form.Item
                name="repeatTimes"
                label="重复运行次数"
                initialValue={1}
                tooltip="同一份参数循环创建 N 个 Job（1-10）；N>1 时 jobName 自动追加 _#i/N 序号方便检索同一批次。"
                rules={[{ type: 'integer', min: 1, max: 10, message: '请输入 1-10 之间的整数' }]}
                style={{ marginBottom: 0 }}
              >
                <InputNumber min={1} max={10} precision={0} style={{ width: 120 }} />
              </Form.Item>
            ),
          }]}
        />
      </Form>
    </Modal>
  );
}
